import React from 'react';
import {ActivityIndicator, View} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {convertTimeControlToMins} from '../../utils';
import styles from './styles';

export interface ChessGameType {
  id: number;
  userid: number;
  platform: string;
  url: string;
  color: string;
  pgn: string;
  time_control: string;
  end_time: string;
  rated: number;
  uuid: string;
  time_class: string;
  result: string;
  reason: string;
  rating: number;
  opponent_rating: number;
  opponent_profile: string;
  opponent_username: string;
  opening: string;
  total_number_of_moves: number;
  castling_index: number;
  opponent_castling_index: number;
  average_time: number;
  opponent_average_time: number;
  time_left_on_clock: number;
  opponent_time_left_on_clock: number;
  time_utilized: number;
  opponent_time_utilized: number;
  total_game_duration_in_minutes: number;
  accuracy: number;
  opponent_accuracy: number;
  analyzed: number;
  unsolved: number;
  annotation_reason: number;
  tournament_id: number;
  round_id: number;
  created_time: string;
  updated_time: string;
  opening_name?: string;
  eco?: string;
  key_concepts?: string[];
  year?: number;
  event?: string;
  model_game_id?: string | number;
  white_player?: string;
  black_player?: string;
}

type OverviewRow = {
  label: string;
  value: string | number | null | undefined;
  wide?: boolean;
};

function getGameOutcomeReason(reasonString: string) {
  if (!reasonString) {
    return '';
  }

  switch (reasonString) {
    case 'timeout':
      return 'Time out';
    case 'timevsinsufficient':
      return 'Insufficient material';
    case 'resigned':
      return 'Resignation';
    case 'repetition':
      return '3-fold repetition';
    case 'abandoned':
      return 'Game abandoned';
    case 'checkmated':
      return 'Checkmated';
    case 'stalemate':
      return 'Stalemate';
    case '50move':
      return '50 move rule';
    case 'insufficient':
      return 'Insufficient material';
    case 'agreed':
      return 'Draw Agreed';
  }
  return reasonString;
}

function formatTime(time: number): string {
  const minutes = Math.floor(time / 60);
  const seconds = Math.round(time % 60);

  if (minutes === 0) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }

  return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${
    seconds !== 1 ? 's' : ''
  }`;
}

function parsePgnHeaders(pgn: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!pgn || typeof pgn !== 'string') {
    return out;
  }
  for (const line of pgn.split('\n')) {
    const t = line.trim();
    if (!t.startsWith('[')) {
      if (t && !/^\[/.test(t)) {
        break;
      }
      continue;
    }
    const m = t.match(/^\[([^\s]+)\s+"([^"]*)"\]$/);
    if (m) {
      out[m[1]] = m[2];
    }
  }
  return out;
}

function formatKeyConceptLabel(raw: string) {
  return String(raw || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function hasValue(value: unknown): boolean {
  return (
    value != null &&
    value !== '' &&
    !(typeof value === 'number' && Number.isNaN(value))
  );
}

function numberText(value: unknown, suffix = ''): string | null {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return null;
  }
  return `${num}${suffix}`;
}

function percentText(value: unknown): string | null {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return null;
  }
  return `${num.toFixed(1)}%`;
}

function dateText(value: unknown): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString();
}

function platformText(platform: unknown): string | null {
  if (!platform) {
    return null;
  }
  return platform === 'Circlechess' ? 'CircleChess' : String(platform);
}

function resultText(game: ChessGameType | Record<string, any>): string | null {
  if (!game?.result) {
    return null;
  }
  if (!game?.username) {
    return String(game.result);
  }
  if (game.result === 'Loss') {
    return `${game.username} Lost`;
  }
  if (game.result === 'Draw') {
    return 'Draw';
  }
  if (game.result === 'Win') {
    return `${game.username} Won`;
  }
  return String(game.result);
}

function playerLine(game: ChessGameType | Record<string, any>): string | null {
  if (!game?.username || !game?.opponent_username) {
    return null;
  }
  const myColor = game?.color;
  const opponentColor = game?.color === 'White' ? 'Black' : 'White';
  const rating = hasValue(game?.rating) ? `, ${game.rating}` : '';
  const opponentRating = hasValue(game?.opponent_rating)
    ? `, ${game.opponent_rating}`
    : '';
  return `${game.username} (${myColor}${rating}) vs ${game.opponent_username} (${opponentColor}${opponentRating})`;
}

function castlingText(
  game: ChessGameType | Record<string, any>,
): string | null {
  if (!game?.username || !game?.opponent_username) {
    return null;
  }
  return `${game.username} ${
    game?.castling_index > 0
      ? `castled on move ${game.castling_index}`
      : 'did not castle'
  }; ${game.opponent_username} ${
    game?.opponent_castling_index > 0
      ? `castled on move ${game.opponent_castling_index}`
      : 'did not castle'
  }`;
}

function clockText(game: ChessGameType | Record<string, any>): string | null {
  if (
    game?.time_left_on_clock == null ||
    game?.opponent_time_left_on_clock == null
  ) {
    return null;
  }
  return `${formatTime(game.time_left_on_clock)} for ${
    game?.username
  }; ${formatTime(game.opponent_time_left_on_clock)} for ${
    game?.opponent_username
  }`;
}

function utilizationText(
  game: ChessGameType | Record<string, any>,
): string | null {
  if (!game?.username || !game?.opponent_username) {
    return null;
  }
  return `${game.username} ${percentText(game?.time_utilized) || '0.0%'}; ${
    game.opponent_username
  } ${percentText(game?.opponent_time_utilized) || '0.0%'}`;
}

function modelRows(
  game: ChessGameType & Record<string, unknown>,
): OverviewRow[] {
  const headers = parsePgnHeaders(game?.pgn || '');
  const openingLine =
    game?.opening_name && game?.eco
      ? `${game.opening_name} (${game.eco})`
      : game?.opening_name || game?.eco || null;
  const keyConceptsFormatted =
    Array.isArray(game?.key_concepts) && game.key_concepts.length
      ? game.key_concepts
          .map((c: string) => formatKeyConceptLabel(c))
          .join(' • ')
      : null;

  return [
    {label: 'Event', value: game?.event ?? headers.Event},
    {label: 'Site', value: headers.Site, wide: true},
    {label: 'Date', value: headers.Date},
    {label: 'Round', value: headers.Round},
    {label: 'Year', value: game?.year},
    {label: 'Opening', value: openingLine, wide: true},
    {
      label: 'White',
      value: game?.white_player || game?.username || headers.White,
    },
    {
      label: 'Black',
      value: game?.black_player || game?.opponent_username || headers.Black,
    },
    {label: 'White Elo', value: headers.WhiteElo},
    {label: 'Black Elo', value: headers.BlackElo},
    {label: 'Result', value: game?.result || headers.Result},
    {label: 'Key concepts', value: keyConceptsFormatted, wide: true},
    {label: 'Ply count', value: headers.PlyCount},
    {label: 'White team', value: headers.WhiteTeam},
    {label: 'Black team', value: headers.BlackTeam},
    {label: 'Model game id', value: game?.model_game_id},
  ].filter(row => hasValue(row.value));
}

function InfoRow({label, value, wide}: OverviewRow) {
  if (!hasValue(value)) {
    return null;
  }
  return (
    <View style={[styles.infoRow, wide ? styles.infoRowWide : null]}>
      <CCText style={styles.infoLabel}>{label}</CCText>
      <CCText style={styles.infoValue}>{value}</CCText>
    </View>
  );
}

function StatTile({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string | number | null | undefined;
}) {
  if (!hasValue(value)) {
    return null;
  }
  return (
    <View style={styles.statTile}>
      <View style={styles.statIcon}>
        <Feather name={icon} size={14} color="#2F8E83" />
      </View>
      <CCText style={styles.statLabel}>{label}</CCText>
      <CCText style={styles.statValue}>{value}</CCText>
    </View>
  );
}

function Section({
  title,
  icon,
  rows,
}: {
  title: string;
  icon: string;
  rows: OverviewRow[];
}) {
  const visibleRows = rows.filter(row => hasValue(row.value));
  if (!visibleRows.length) {
    return null;
  }

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Feather name={icon} size={15} color="#2F8E83" />
        <CCText style={styles.sectionTitle}>{title}</CCText>
      </View>
      <View style={styles.sectionRows}>
        {visibleRows.map(row => (
          <InfoRow key={row.label} {...row} />
        ))}
      </View>
    </View>
  );
}

function EmptyState() {
  const {isMobileOrTab} = useMediaQuery();
  return (
    <Box style={styles.loadingWrap} overflow="hidden">
      <ActivityIndicator
        size="small"
        color={CCColors.Green}
        style={{marginTop: !isMobileOrTab ? 200 : 10}}
      />
    </Box>
  );
}

function ModelOverview({
  gameData,
}: {
  gameData: ChessGameType & Record<string, unknown>;
}) {
  const rows = modelRows(gameData);
  if (!rows.length) {
    return <EmptyState />;
  }

  const title =
    gameData?.opening_name || gameData?.eco || gameData?.event || 'Model game';
  return (
    <View style={styles.container}>
      <View style={styles.hero}>
        <View style={styles.heroTitleRow}>
          <View style={styles.heroIcon}>
            <Feather name="book-open" size={17} color="#2F8E83" />
          </View>
          <CCText style={styles.heroKicker}>Overview</CCText>
        </View>
        <CCText style={styles.heroTitle}>{title}</CCText>
      </View>
      <Section title="Game details" icon="list" rows={rows} />
    </View>
  );
}

function GameOverview({
  gameData,
}: {
  gameData: ChessGameType | Record<string, never>;
}) {
  if (!gameData || Object.keys(gameData).length === 0) {
    return <EmptyState />;
  }

  if (gameData?.platform === 'ModelGame') {
    return <ModelOverview gameData={gameData as ChessGameType} />;
  }

  const result = resultText(gameData);
  const players = playerLine(gameData);
  const reason = getGameOutcomeReason(gameData?.reason || gameData?.end_type);
  const timeControl = gameData?.time_control
    ? convertTimeControlToMins(gameData.time_control)
    : null;

  const gameRows: OverviewRow[] = [
    {label: 'Game ID', value: gameData?.id},
    {label: 'Platform', value: platformText(gameData?.platform)},
    {label: 'Date', value: dateText(gameData?.end_time)},
    {label: 'Game URL', value: gameData?.url, wide: true},
    {label: 'Opening', value: gameData?.opening, wide: true},
  ];

  const timeRows: OverviewRow[] = [
    {label: 'Time control', value: timeControl},
    {
      label: 'Average time',
      value: numberText(gameData?.average_time, ' seconds'),
    },
    {label: 'Clock left', value: clockText(gameData), wide: true},
    {
      label: 'Total duration',
      value: numberText(gameData?.total_game_duration_in_minutes, ' minutes'),
    },
    {label: 'Time utilization', value: utilizationText(gameData), wide: true},
  ];

  const outcomeRows: OverviewRow[] = [
    {label: 'Players', value: players, wide: true},
    {label: 'Result', value: result},
    {label: 'Reason', value: reason},
    {label: 'Castling', value: castlingText(gameData), wide: true},
  ];

  return (
    <View style={styles.container}>
      <View style={styles.hero}>
        <View style={styles.heroTitleRow}>
          <View style={styles.heroIcon}>
            <Feather name="activity" size={17} color="#2F8E83" />
          </View>
          <CCText style={styles.heroKicker}>Overview</CCText>
        </View>
        <CCText style={styles.heroTitle}>{result || 'Game summary'}</CCText>
        {players ? (
          <CCText style={styles.heroSubtitle}>{players}</CCText>
        ) : null}
      </View>

      <View style={styles.statGrid}>
        <StatTile
          icon="hash"
          label="Moves"
          value={gameData?.total_number_of_moves}
        />
        <StatTile icon="clock" label="Time" value={timeControl} />
        <StatTile
          icon="zap"
          label="Avg move"
          value={numberText(gameData?.average_time, 's')}
        />
        <StatTile
          icon="percent"
          label="Used"
          value={percentText(gameData?.time_utilized)}
        />
      </View>

      <Section title="Result" icon="award" rows={outcomeRows} />
      <Section title="Game details" icon="list" rows={gameRows} />
      <Section title="Time" icon="clock" rows={timeRows} />
    </View>
  );
}

export default GameOverview;
